import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { AuthContext } from '../context/AuthContext';
import { TenantContext } from '../context/TenantContext';
import GradePicker from '../components/GradePicker/GradePicker';
import { Icon } from '../components/SvgIcons';
import MyTemplatesList from '../components/MyTemplatesList';
import './PaperTemplates.css';

// K-12 (school) tenants restrict the academic grade to senior classes. Other
// tenant types (university/college/enterprise) use their own academic schema
// — never force these "Class N" keys on them or the picker/distribution breaks.
const SCHOOL_CLASS_LEVELS = ['Class 11', 'Class 12'];

const CreateTemplateManual = ({ onSuccess, onCancel, embedded = false }) => {
  const { showToast } = useToast();
  const { user } = useContext(AuthContext) || {};
  const { tenantType } = useContext(TenantContext) || {};
  const allowedClassLevels = tenantType === 'school' ? SCHOOL_CLASS_LEVELS : null;
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [autoAdjustEnabled, setAutoAdjustEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
    questions: [],
    difficulty_weightage: { easy: 30, medium: 50, hard: 20 },
    is_shared: false
  });

  const isModal = onSuccess && onCancel;
  const isFullPage = !isModal;

  const getDifficultyWeightage = () => formData.difficulty_weightage || { easy: 30, medium: 50, hard: 20 };

  const handleAddQuestion = () => {
    const nextNumber = formData.questions.length + 1;
    // Class level is synchronized across the whole template, so a new question
    // inherits the shared class level already chosen on the existing questions.
    const sharedClassLevel = formData.questions[0]?.class_level || '';
    setFormData({
      ...formData,
      questions: [...formData.questions, {
        question_number: `Q${nextNumber}`,
        question_type: 'mcq',
        difficulty: 'medium',
        class_level: sharedClassLevel,
        has_subquestions: false,
        marks: 5,
        subquestions: []
      }]
    });
  };

  const handleRemoveQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    const renumbered = newQuestions.map((q, i) => ({ ...q, question_number: `Q${i + 1}` }));
    setFormData({ ...formData, questions: renumbered });
  };

  // Class level is template-wide: applying it to one question mirrors it to
  // every question and sub-question so they stay in lockstep.
  // Roman numerals for the 3rd level (i, ii, iii…) to distinguish from the
  // 2nd-level letters (a, b, c…).
  const ROMAN = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x', 'xi', 'xii'];
  const romanFor = (n) => ROMAN[n] || `${n + 1}`;

  const syncClassLevel = (questions, value) =>
    questions.map(q => ({
      ...q,
      class_level: value,
      subquestions: (q.subquestions || []).map(sq => ({
        ...sq,
        class_level: value,
        subquestions: (sq.subquestions || []).map(ssq => ({ ...ssq, class_level: value })),
      })),
    }));

  const handleQuestionChange = (index, field, value) => {
    let newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    if (field === 'has_subquestions' && value && newQuestions[index].subquestions.length === 0) {
      newQuestions[index].subquestions = [{ sub_number: 'a', marks: 2, difficulty: newQuestions[index].difficulty || 'medium', class_level: newQuestions[index].class_level || '' }];
    }
    if (field === 'class_level') {
      newQuestions = syncClassLevel(newQuestions, value);
    }
    const updatedFormData = { ...formData, questions: newQuestions };
    if (field === 'difficulty' && !autoAdjustEnabled) {
      const actualDist = calculateActualDistribution(newQuestions);
      updatedFormData.difficulty_weightage = { easy: actualDist.easy, medium: actualDist.medium, hard: actualDist.hard };
    }
    setFormData(updatedFormData);
  };

  const handleAddSubQuestion = (qIndex) => {
    const newQuestions = [...formData.questions];
    const subCount = newQuestions[qIndex].subquestions.length;
    const nextLetter = String.fromCharCode(97 + subCount);
    newQuestions[qIndex].subquestions.push({
      sub_number: nextLetter,
      marks: 2,
      difficulty: newQuestions[qIndex].difficulty || 'medium',
      class_level: newQuestions[qIndex].class_level || ''
    });
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveSubQuestion = (qIndex, sqIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions = newQuestions[qIndex].subquestions.filter((_, i) => i !== sqIndex);
    newQuestions[qIndex].subquestions = newQuestions[qIndex].subquestions.map((sq, i) => ({ ...sq, sub_number: String.fromCharCode(97 + i) }));
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleSubQuestionChange = (qIndex, sqIndex, field, value) => {
    let newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions[sqIndex] = { ...newQuestions[qIndex].subquestions[sqIndex], [field]: value };
    // Toggling "has sub-questions" on a sub-question seeds the 3rd level.
    if (field === 'has_subquestions' && value) {
      const sq = newQuestions[qIndex].subquestions[sqIndex];
      if (!sq.subquestions || sq.subquestions.length === 0) {
        sq.subquestions = [{ sub_number: 'i', marks: 1, difficulty: sq.difficulty || 'medium', class_level: sq.class_level || '' }];
      }
    }
    if (field === 'class_level') {
      newQuestions = syncClassLevel(newQuestions, value);
    }
    const updatedFormData = { ...formData, questions: newQuestions };
    if ((field === 'difficulty' || field === 'has_subquestions') && !autoAdjustEnabled) {
      const actualDist = calculateActualDistribution(newQuestions);
      updatedFormData.difficulty_weightage = { easy: actualDist.easy, medium: actualDist.medium, hard: actualDist.hard };
    }
    setFormData(updatedFormData);
  };

  // ── 3rd-level (sub-sub-question) handlers ──
  const handleAddNestedSub = (qIndex, sqIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[qIndex].subquestions[sqIndex];
    sq.subquestions = sq.subquestions || [];
    sq.subquestions.push({
      sub_number: romanFor(sq.subquestions.length),
      marks: 1,
      difficulty: sq.difficulty || 'medium',
      class_level: sq.class_level || '',
    });
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleRemoveNestedSub = (qIndex, sqIndex, ssqIndex) => {
    const newQuestions = [...formData.questions];
    const sq = newQuestions[qIndex].subquestions[sqIndex];
    sq.subquestions = sq.subquestions.filter((_, i) => i !== ssqIndex).map((ssq, i) => ({ ...ssq, sub_number: romanFor(i) }));
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleNestedSubChange = (qIndex, sqIndex, ssqIndex, field, value) => {
    let newQuestions = [...formData.questions];
    newQuestions[qIndex].subquestions[sqIndex].subquestions[ssqIndex] = {
      ...newQuestions[qIndex].subquestions[sqIndex].subquestions[ssqIndex], [field]: value,
    };
    if (field === 'class_level') newQuestions = syncClassLevel(newQuestions, value);
    const updatedFormData = { ...formData, questions: newQuestions };
    if (field === 'difficulty' && !autoAdjustEnabled) {
      const actualDist = calculateActualDistribution(newQuestions);
      updatedFormData.difficulty_weightage = { easy: actualDist.easy, medium: actualDist.medium, hard: actualDist.hard };
    }
    setFormData(updatedFormData);
  };

  // Marks of a sub-question = sum of its 3rd-level parts if it has any, else its own.
  const subMarks = (sq) => (sq.has_subquestions && sq.subquestions && sq.subquestions.length)
    ? sq.subquestions.reduce((s, ssq) => s + (parseInt(ssq.marks) || 0), 0)
    : (parseInt(sq.marks) || 0);

  const getTotalMarks = () => {
    return formData.questions.reduce((sum, q) => {
      if (q.has_subquestions) return sum + q.subquestions.reduce((subSum, sq) => subSum + subMarks(sq), 0);
      return sum + (parseInt(q.marks) || 0);
    }, 0);
  };

  const calculateActualDistribution = (questions) => {
    let easyMarks = 0, mediumMarks = 0, hardMarks = 0;
    const bucket = (marks, difficulty) => {
      const m = parseInt(marks) || 0;
      if (difficulty === 'easy') easyMarks += m;
      else if (difficulty === 'hard') hardMarks += m;
      else mediumMarks += m;
    };
    questions.forEach(q => {
      if (q.has_subquestions && q.subquestions) {
        q.subquestions.forEach(sq => {
          if (sq.has_subquestions && sq.subquestions && sq.subquestions.length) {
            sq.subquestions.forEach(ssq => bucket(ssq.marks, ssq.difficulty));
          } else {
            bucket(sq.marks, sq.difficulty);
          }
        });
      } else {
        bucket(q.marks, q.difficulty);
      }
    });
    const totalMarks = getTotalMarks();
    if (totalMarks === 0) return { easy: 0, medium: 0, hard: 0 };
    return { easy: Math.round((easyMarks / totalMarks) * 100), medium: Math.round((mediumMarks / totalMarks) * 100), hard: Math.round((hardMarks / totalMarks) * 100) };
  };

  const handleWeightageChange = (level, value) => {
    const currentWeightage = getDifficultyWeightage();
    const newWeightage = { ...currentWeightage, [level]: value };
    setFormData({ ...formData, difficulty_weightage: newWeightage });
  };

  const handleSave = async () => {
    if (!formData.template_name.trim()) { showToast('Please enter a template name', 'error'); return; }
    if (formData.questions.length === 0) { showToast('Please add at least one question', 'error'); return; }
    const weightage = formData.difficulty_weightage || { easy: 30, medium: 50, hard: 20 };
    const totalWeightage = (weightage.easy || 0) + (weightage.medium || 0) + (weightage.hard || 0);
    if (totalWeightage !== 100) { showToast(`Difficulty weightage must total 100%. Current total: ${totalWeightage}%`, 'error'); return; }
    setSaving(true);
    try {
      const dataToSave = { template_name: formData.template_name, description: formData.description, questions: formData.questions, difficulty_weightage: weightage, is_shared: formData.is_shared };
      const { data } = await API.post('/templates', dataToSave);
      if (data.success) {
        showToast('Template created successfully!', 'success');
        if (onSuccess) onSuccess(data.template_id);
        else navigate('/paper-templates');
      }
    } catch (error) {
      console.error('Save error:', error);
      showToast(error.response?.data?.message || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBackClick = () => {
    if (onCancel) onCancel();
    else navigate(-1);
  };

  const iconBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem' };

  return (
    <div className={embedded ? 'create-template-container' : (isFullPage ? 'dashboard' : 'create-template-container')}>
      {isFullPage && !embedded && (
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <Icon name="Clipboard" size={26} />
              <span>Create Question Template</span>
            </h1>
            <p className="dashboard-subtitle">Create a new question paper template from scratch</p>
          </div>
          <button className="btn btn-secondary" style={iconBtn} onClick={handleBackClick}>
            <Icon name="ArrowLeft" size={16} />
            <span>Back</span>
          </button>
        </div>
      )}

      <div className="card">
        {!isFullPage && (
          <div className="card-header">
            <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Icon name="Edit" size={20} />
              <span>Create New Template</span>
            </h2>
            {onCancel && (
              <button className="btn btn-secondary btn-sm" style={iconBtn} onClick={onCancel}>
                <Icon name="ArrowLeft" size={14} />
                <span>Back</span>
              </button>
            )}
          </div>
        )}

        {!embedded && (
          <div className="tpl-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'create'}
              className={`tpl-tab${activeTab === 'create' ? ' active' : ''}`}
              onClick={() => setActiveTab('create')}
            >
              <Icon name="Plus" />
              <span>Create</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'my-templates'}
              className={`tpl-tab${activeTab === 'my-templates' ? ' active' : ''}`}
              onClick={() => setActiveTab('my-templates')}
            >
              <Icon name="FileText" />
              <span>My Templates</span>
            </button>
          </div>
        )}

        {(!embedded && activeTab === 'my-templates') ? (
          <MyTemplatesList />
        ) : (
        <form style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Template Name *</label>
            <input type="text" className="form-input" value={formData.template_name} onChange={(e) => setFormData({ ...formData, template_name: e.target.value })} placeholder="e.g., Mid-Term Exam Template" required />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Description</label>
            <textarea className="form-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description..." rows={2} />
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: 'var(--bg-secondary)', borderRadius: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Icon name="Scale" size={18} />
                <span>Difficulty Weightage</span>
              </h4>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input type="checkbox" checked={autoAdjustEnabled} onChange={(e) => setAutoAdjustEnabled(e.target.checked)} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Icon name={autoAdjustEnabled ? 'Refresh' : 'Hand'} size={14} />
                  <span>{autoAdjustEnabled ? 'Auto-Adjust' : 'Manual'}</span>
                </span>
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <label className="form-label">Easy (%)</label>
                <input type="number" className="form-input" value={getDifficultyWeightage().easy} onChange={(e) => handleWeightageChange('easy', parseInt(e.target.value) || 0)} min="0" max="100" />
              </div>
              <div>
                <label className="form-label">Medium (%)</label>
                <input type="number" className="form-input" value={getDifficultyWeightage().medium} onChange={(e) => handleWeightageChange('medium', parseInt(e.target.value) || 0)} min="0" max="100" />
              </div>
              <div>
                <label className="form-label">Hard (%)</label>
                <input type="number" className="form-input" value={getDifficultyWeightage().hard} onChange={(e) => handleWeightageChange('hard', parseInt(e.target.value) || 0)} min="0" max="100" />
              </div>
            </div>
          </div>

          {user?.role === 'examiner' && (
            <div style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', background: 'var(--bg-secondary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Share with other examiners</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>Other examiners can view and use this template</div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.is_shared} onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: '12px', transition: '0.3s',
                  backgroundColor: formData.is_shared ? '#3b82f6' : '#cbd5e1'
                }}>
                  <span style={{
                    position: 'absolute', left: formData.is_shared ? '22px' : '3px', top: '3px',
                    width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </span>
              </label>
            </div>
          )}

          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Icon name="FileText" size={20} />
                <span>Questions (Total Marks: {getTotalMarks()})</span>
              </h3>
              <button type="button" className="btn btn-primary btn-sm" style={iconBtn} onClick={handleAddQuestion}>
                <Icon name="Plus" size={14} />
                <span>Add Question</span>
              </button>
            </div>

            {formData.questions.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: '8px', color: 'var(--text-secondary)' }}>
                <p>No questions added yet. Click "Add Question" to get started.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {formData.questions.map((question, qIndex) => (
                  <div key={qIndex} style={{ padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ margin: 0 }}>{question.question_number}</h4>
                      <button type="button" className="btn btn-danger btn-sm" style={iconBtn} onClick={() => handleRemoveQuestion(qIndex)}>
                        <Icon name="Trash" size={14} />
                        <span>Remove</span>
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label className="form-label">Type</label>
                        <select className="form-input" value={question.question_type} onChange={(e) => handleQuestionChange(qIndex, 'question_type', e.target.value)}>
                          <option value="mcq">MCQ</option>
                          <option value="short_answer">Short Answer</option>
                          <option value="long_answer">Long Answer</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Difficulty</label>
                        <select className="form-input" value={question.difficulty} onChange={(e) => handleQuestionChange(qIndex, 'difficulty', e.target.value)}>
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Marks</label>
                        <input type="number" className="form-input" value={question.marks} onChange={(e) => handleQuestionChange(qIndex, 'marks', parseInt(e.target.value) || 0)} min="1" disabled={question.has_subquestions} />
                      </div>
                      <div>
                        <label className="form-label">Class Level</label>
                        <GradePicker
                          value={question.class_level || ''}
                          onChange={(classLevel) => handleQuestionChange(qIndex, 'class_level', classLevel)}
                          allowedLevelKeys={allowedClassLevels}
                          compact
                        />
                      </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '1rem' }}>
                      <input type="checkbox" checked={question.has_subquestions} onChange={(e) => handleQuestionChange(qIndex, 'has_subquestions', e.target.checked)} />
                      <span>Has Sub-questions</span>
                    </label>

                    {question.has_subquestions && question.subquestions && (
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <h5 style={{ margin: 0 }}>Sub-questions</h5>
                          <button type="button" className="btn btn-primary btn-sm" style={iconBtn} onClick={() => handleAddSubQuestion(qIndex)}>
                            <Icon name="Plus" size={12} />
                            <span>Add Sub-question</span>
                          </button>
                        </div>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {question.subquestions.map((subq, sqIndex) => (
                            <div key={sqIndex} style={{ border: '1px dashed var(--border-color)', borderRadius: '8px', padding: '0.5rem' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'end' }}>
                                <input type="text" className="form-input" value={subq.sub_number} onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'sub_number', e.target.value)} placeholder="a, b, c..." style={{ fontSize: '0.875rem' }} />
                                <input type="number" className="form-input" value={subMarks(subq)} onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'marks', parseInt(e.target.value) || 0)} min="1" placeholder="Marks" disabled={subq.has_subquestions} style={{ fontSize: '0.875rem' }} />
                                <select className="form-input" value={subq.difficulty} onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'difficulty', e.target.value)} style={{ fontSize: '0.875rem' }}>
                                  <option value="easy">Easy</option>
                                  <option value="medium">Medium</option>
                                  <option value="hard">Hard</option>
                                </select>
                                <GradePicker
                                  value={subq.class_level || ''}
                                  onChange={(classLevel) => handleSubQuestionChange(qIndex, sqIndex, 'class_level', classLevel)}
                                  allowedLevelKeys={allowedClassLevels}
                                  compact
                                />
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveSubQuestion(qIndex, sqIndex)} style={{ padding: '0.5rem 0.75rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Remove sub-question">
                                  <Icon name="X" size={14} />
                                </button>
                              </div>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', margin: '0.5rem 0 0', fontSize: '0.8rem' }}>
                                <input type="checkbox" checked={!!subq.has_subquestions} onChange={(e) => handleSubQuestionChange(qIndex, sqIndex, 'has_subquestions', e.target.checked)} />
                                <span>Has sub-parts</span>
                              </label>

                              {subq.has_subquestions && subq.subquestions && (
                                <div style={{ marginTop: '0.5rem', paddingLeft: '1rem', borderLeft: '2px solid var(--border-color)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Sub-parts</span>
                                    <button type="button" className="btn btn-primary btn-sm" style={iconBtn} onClick={() => handleAddNestedSub(qIndex, sqIndex)}>
                                      <Icon name="Plus" size={11} />
                                      <span>Add Sub-part</span>
                                    </button>
                                  </div>
                                  <div style={{ display: 'grid', gap: '0.4rem' }}>
                                    {subq.subquestions.map((ssq, ssqIndex) => (
                                      <div key={ssqIndex} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '0.4rem', alignItems: 'end' }}>
                                        <input type="text" className="form-input" value={ssq.sub_number} onChange={(e) => handleNestedSubChange(qIndex, sqIndex, ssqIndex, 'sub_number', e.target.value)} placeholder="i, ii, iii..." style={{ fontSize: '0.82rem' }} />
                                        <input type="number" className="form-input" value={ssq.marks} onChange={(e) => handleNestedSubChange(qIndex, sqIndex, ssqIndex, 'marks', parseInt(e.target.value) || 0)} min="1" placeholder="Marks" style={{ fontSize: '0.82rem' }} />
                                        <select className="form-input" value={ssq.difficulty} onChange={(e) => handleNestedSubChange(qIndex, sqIndex, ssqIndex, 'difficulty', e.target.value)} style={{ fontSize: '0.82rem' }}>
                                          <option value="easy">Easy</option>
                                          <option value="medium">Medium</option>
                                          <option value="hard">Hard</option>
                                        </select>
                                        <GradePicker
                                          value={ssq.class_level || ''}
                                          onChange={(classLevel) => handleNestedSubChange(qIndex, sqIndex, ssqIndex, 'class_level', classLevel)}
                                          allowedLevelKeys={allowedClassLevels}
                                          compact
                                        />
                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveNestedSub(qIndex, sqIndex, ssqIndex)} style={{ padding: '0.4rem 0.6rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Remove sub-part">
                                          <Icon name="X" size={13} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            {onCancel && <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>}
            {!isModal && <button type="button" className="btn btn-secondary" onClick={handleBackClick}>Back</button>}
            <button type="button" className="btn btn-primary" style={iconBtn} onClick={handleSave} disabled={saving}>
              <Icon name="Save" size={16} />
              <span>{saving ? 'Saving...' : 'Save Template'}</span>
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default CreateTemplateManual;
